import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { CREDIT_PACKS, DEFAULT_CREDIT_PACK_ID, OfferedCreditPack } from 'fym-shared/src/music24/creditPacks';
import { music24PlanCapabilities, Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { planNameMessages } from '../../config/music24PlanNames';
import { useVideoCredits } from './useVideoCredits';
import { useBuyCreditPack } from './useBuyCreditPack';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { CreditPackOption } from './CreditPackOption';
import { MemberOwnerNote } from './MemberOwnerNote';
import { creditMessages } from './creditMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
import { useDisplayCurrency } from '../../config/useDisplayCurrency';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the video credits; each part uses its own slice of it */
import styles from './Credits.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The plan the upgrade line offers: the next plan with more video credits a month. Free, the trial
 * and Artist go to Pro (full-song videos and 100 credits), Pro goes to Manager. Plug is never the
 * offer here: it has the video credits of Pro. A Plug workspace goes to Label, the one bigger plan
 * that keeps autopilot.
 */
export const NEXT_TIER: Record<Music24PlanTier, Music24PlanTier | null> = {
  free: 'pro',
  trial: 'pro',
  artist: 'pro',
  pro: 'manager',
  plug: 'label',
  manager: 'label',
  label: null,
};

/** The pack the modal selects first ("Buy 30 for $39"). */
const DEFAULT_PACK: OfferedCreditPack =
  CREDIT_PACKS.find((pack) => pack.id === DEFAULT_CREDIT_PACK_ID) ?? (CREDIT_PACKS[0] as OfferedCreditPack);

interface CreditPacksModalProps {
  isOpen: boolean;
  accountId: number;
  onClose: () => void;
  /** Opens the plan upgrade (the upgrade prompt of the video credits). */
  onUpgrade: () => void;
}

/**
 * Out of video credits (design release-kit g8, n7): three packs that never expire, "Buy 30 for
 * $39" and the upgrade line. The owner buys; a member sees the owner note (e4) and no button.
 * Only a paid plan sees the packs (pricing spec 2026-10-08 §5): `GlobalSMLUpgradeModal` sends Free
 * and the trial to the plan offer, and this modal shows them no pack and no buy button.
 *
 * Inside a native app's web view (App Store 3.1.1) the modal shows no pack, no price and no
 * purchase link. It shows a neutral line in their place, as the upgrade prompt does.
 */
const CreditPacksModalComponent: React.FC<CreditPacksModalProps> = ({ isOpen, accountId, onClose, onUpgrade }) => {
  const intl = useIntl();
  const { credits } = useVideoCredits({ accountId });
  const { canBuy, loadingPackId, buy } = useBuyCreditPack();
  const isNativeEmbed = useIsNativeEmbed();
  const [selected, setSelected] = useState<OfferedCreditPack>(DEFAULT_PACK);
  // The symbol of the visitor; the Checkout charges the same currency (QA finding D12).
  const currency = useDisplayCurrency();

  const plan = credits?.plan ?? 'free';
  const isPaidPlan = plan !== 'free' && plan !== 'trial';
  const isOwner = credits?.isOwner ?? true;
  const nextTier = NEXT_TIER[plan];
  const nextCount = nextTier === null ? null : music24PlanCapabilities[nextTier].videoCreditsPerMonth;
  const price = intl.formatNumber(selected.priceUsd, { style: 'currency', currency, maximumFractionDigits: 0 });

  const title =
    credits === null || credits.limit === null
      ? intl.formatMessage(creditMessages.modalTitleNoLimit)
      : intl.formatMessage(creditMessages.modalTitle, { used: credits.used, limit: credits.limit });

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody className="d-flex flex-column gap-3">
        {credits !== null && (
          <p className="mb-0">
            {intl.formatMessage(isNativeEmbed ? creditMessages.modalBodyNative : creditMessages.modalBody, {
              date: intl.formatDate(credits.resetsAt, { day: 'numeric', month: 'short' }),
            })}
          </p>
        )}
        {!isNativeEmbed && isPaidPlan && (
          <div className={styles.packs} role="radiogroup">
            {CREDIT_PACKS.map((pack) => (
              <CreditPackOption key={pack.id} pack={pack} isSelected={pack.id === selected.id} onSelect={setSelected} />
            ))}
          </div>
        )}
        {isOwner && nextTier !== null && nextCount !== null && (
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span className={styles.muted}>
              {intl.formatMessage(creditMessages.upgradeLine, {
                plan: intl.formatMessage(planNameMessages[nextTier]),
                count: nextCount,
              })}
            </span>
            <SMLButton outline omitCap onClick={onUpgrade}>
              {intl.formatMessage(creditMessages.upgrade)}
            </SMLButton>
          </div>
        )}
        {!isOwner && <MemberOwnerNote />}
        {isNativeEmbed && <p className={`${styles.muted} mb-0`}>{intl.formatMessage(creditMessages.nativeNoPacks)}</p>}
        {!isNativeEmbed && isOwner && isPaidPlan && !canBuy && (
          <p className={`${styles.muted} mb-0`}>{withBrandWord(intl.formatMessage(creditMessages.buyInBrowser))}</p>
        )}
        {!isNativeEmbed && isOwner && isPaidPlan && canBuy && (
          <SMLButton
            color="primary"
            stretch
            omitCap
            isLoading={loadingPackId === selected.id}
            disabled={loadingPackId !== null}
            onClick={() => {
              buy(selected.id).catch(console.error);
            }}
          >
            {intl.formatMessage(creditMessages.buy, { count: selected.credits, price })}
          </SMLButton>
        )}
      </ModalBody>
    </Modal>
  );
};

export const CreditPacksModal = TypedMemo(CreditPacksModalComponent);
